import React, { useState, useEffect } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { NavigationBar } from './NavigationBar';
import logoImg from './media/img/bbLogoClear.png';

// Logo imports for conveyor belt
import colgateLogo from './media/img/colgateLogobb.png';
import amfamLogo from './media/img/amfamLogobb.png';
import palmoliveLogo from './media/img/palmoliveLogobb.png';
import seacretLogo from './media/img/seacretLogobb.png';
import classDojLogo from './media/img/classDojLogobbW.png';
import impactiveLogo from './media/img/impactivebb.png';
import bioplusLogo from './media/img/bioplusLogobb.png';
import pmInterLogo from './media/img/pmInterbb.png';
import socialrootsLogo from './media/img/socialrootsbb.png';
import elevateHTLogo from './media/img/elevateHTbb.png';
import patternAgLogo from './media/img/patternAgbb.png';
import zinzinoLogo from './media/img/zinzinoLogobb.png';

const MashkraftHomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);

  const slides = [
    {
      title: "Innovative solutions for a changing world",
      description: "Businesses rely on us to help them obtain a competitive advantage. Whether it's by developing a minimum viable product, releasing a new app first to market, analysing data to make better decisions, or designing an interactive website, we do it with a passion for the consumer and a fascination with technology."
    },
    {
      title: "Building tomorrow's digital experiences",
      description: "We transform ideas into digital platforms with an accelerated time-to-market. Our innovative strategies drive real business value through optimization and fast development cycles. We are not just developers but strategic partners in your digital journey."
    }
  ];

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Auto-slide
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  // Preload Calendly script
  useEffect(() => {
    const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
    
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.onload = () => setCalendlyLoaded(true);
      document.head.appendChild(script);
    } else {
      setCalendlyLoaded(true);
    }
  }, []);

  // Initialize Calendly widget when modal opens
  useEffect(() => {
    if (isModalOpen && calendlyLoaded && window.Calendly) {
      const calendlyElement = document.querySelector('.calendly-inline-widget');
      if (calendlyElement) {
        window.Calendly.initInlineWidget({
          url: 'https://calendly.com/bb-global-solutions/discovery',
          parentElement: calendlyElement,
          prefill: {},
          utm: {}
        });
      }
    }
  }, [isModalOpen, calendlyLoaded]);

  // Listen for navigation modal event
  useEffect(() => {
    const handleOpenModal = () => setIsModalOpen(true);
    window.addEventListener('openCalendlyModal', handleOpenModal);
    return () => window.removeEventListener('openCalendlyModal', handleOpenModal);
  }, []);

  // Logo array for conveyor belt
  const logos = [
    colgateLogo, amfamLogo, palmoliveLogo, seacretLogo, classDojLogo, impactiveLogo,
    bioplusLogo, pmInterLogo, socialrootsLogo, elevateHTLogo, patternAgLogo, zinzinoLogo,
  ];

  return (
    <>
      <GlobalStyle />
      <PageWrapper>
        {/* Navigation */}
        <NavWrapper>
          <NavigationBar />
        </NavWrapper>

        {/* Hero Section */}
        <HeroSection>
          <GeometricBackground>
            <GeometricPattern />
          </GeometricBackground>
          
          <HeroContent>
            <HeroTextContent>
              <HeroTitle key={currentSlide}>
                {slides[currentSlide].title}
              </HeroTitle>
              <HeroDescription key={`desc-${currentSlide}`}>
                {slides[currentSlide].description}
              </HeroDescription>
              <HeroButtons>
                <PrimaryButton onClick={openModal}>
                  Get Started
                  <ButtonArrow>→</ButtonArrow>
                </PrimaryButton>
                <SecondaryButton href="/classic">
                  Learn More
                </SecondaryButton>
              </HeroButtons>
            </HeroTextContent>
            
            <HeroImageContainer>
              <HeroPersonImage>
                <img src={logoImg} alt="B&B Global Solutions" />
              </HeroPersonImage>
              <FloatingCard style={{ top: '10%', right: '5%' }}>
                <FloatingIcon>🚀</FloatingIcon>
                <FloatingText>Fast Delivery</FloatingText>
              </FloatingCard>
              <FloatingCard style={{ bottom: '20%', left: '0%' }}>
                <FloatingIcon>💡</FloatingIcon>
                <FloatingText>Innovation</FloatingText>
              </FloatingCard>
              <FloatingCard style={{ top: '50%', right: '0%' }}>
                <FloatingIcon>⚡</FloatingIcon>
                <FloatingText>AI Powered</FloatingText>
              </FloatingCard>
            </HeroImageContainer>
          </HeroContent>

          <SliderDots>
            {slides.map((_, index) => (
              <Dot 
                key={index} 
                $active={index === currentSlide}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </SliderDots>
        </HeroSection>

        {/* Logo Strip */}
        <LogoStripSection>
          <LogoTrack>
            {logos.map((logo, index) => (
              <LogoItem key={index}>
                <LogoImage src={logo} alt={`Partner ${index + 1}`} />
              </LogoItem>
            ))}
            {logos.map((logo, index) => (
              <LogoItem key={`dup-${index}`}>
                <LogoImage src={logo} alt={`Partner ${index + 1}`} />
              </LogoItem>
            ))}
          </LogoTrack>
        </LogoStripSection>

        {/* Trusted By Section */}
        <TrustedSection>
          <TrustedContainer>
            <TrustedContent>
              <SectionLabel>WHY CHOOSE US</SectionLabel>
              <TrustedTitle>Trusted by Leading Companies across the World</TrustedTitle>
              <TrustedDescription>
                We transform ideas into digital platforms with an accelerated time-to-market. 
                Our innovative strategies drive real business value through optimization and fast 
                development cycles. We are not just developer but strategic partners committed to your success.
              </TrustedDescription>
              <TrustedFeatures>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <FeatureText>AI-Powered Solutions</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <FeatureText>Rapid Development</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <FeatureText>24/7 Support</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <FeatureText>Custom Integrations</FeatureText>
                </FeatureItem>
              </TrustedFeatures>
              <CTAButton onClick={openModal}>
                Schedule Consultation
              </CTAButton>
            </TrustedContent>
            <TrustedVisual>
              <DashboardMockup>
                <DashboardHeader>
                  <DashboardDots>
                    <Dot $color="#ff5f56" />
                    <Dot $color="#ffbd2e" />
                    <Dot $color="#27ca40" />
                  </DashboardDots>
                </DashboardHeader>
                <DashboardContent>
                  <DashboardSidebar>
                    <SidebarItem $active>📊 Dashboard</SidebarItem>
                    <SidebarItem>📈 Analytics</SidebarItem>
                    <SidebarItem>⚙️ Settings</SidebarItem>
                    <SidebarItem>👥 Team</SidebarItem>
                  </DashboardSidebar>
                  <DashboardMain>
                    <StatCard>
                      <StatValue>$247K</StatValue>
                      <StatLabel>Revenue</StatLabel>
                    </StatCard>
                    <StatCard>
                      <StatValue>1,847</StatValue>
                      <StatLabel>Users</StatLabel>
                    </StatCard>
                    <ChartPlaceholder>
                      <ChartBar style={{ height: '40%' }} />
                      <ChartBar style={{ height: '65%' }} />
                      <ChartBar style={{ height: '45%' }} />
                      <ChartBar style={{ height: '80%' }} />
                      <ChartBar style={{ height: '55%' }} />
                      <ChartBar style={{ height: '90%' }} />
                      <ChartBar style={{ height: '70%' }} />
                    </ChartPlaceholder>
                  </DashboardMain>
                </DashboardContent>
              </DashboardMockup>
              <FloatingElement style={{ top: '-20px', right: '-20px' }}>
                <PlantIcon>🌱</PlantIcon>
              </FloatingElement>
            </TrustedVisual>
          </TrustedContainer>
        </TrustedSection>

        {/* Services Section */}
        <ServicesSection>
          <ServicesContainer>
            <SectionLabel>WHAT WE DO</SectionLabel>
            <ServicesTitle>Our Core Services</ServicesTitle>
            <ServicesGrid>
              <ServiceCard>
                <ServiceIconBox>
                  <ServiceIcon>🤖</ServiceIcon>
                </ServiceIconBox>
                <ServiceTitle>AI Integration</ServiceTitle>
                <ServiceDescription>
                  Leverage cutting-edge AI technology to automate processes and gain competitive insights.
                </ServiceDescription>
              </ServiceCard>
              <ServiceCard>
                <ServiceIconBox>
                  <ServiceIcon>💻</ServiceIcon>
                </ServiceIconBox>
                <ServiceTitle>Web Development</ServiceTitle>
                <ServiceDescription>
                  Modern, responsive websites built with the latest technologies for optimal performance.
                </ServiceDescription>
              </ServiceCard>
              <ServiceCard>
                <ServiceIconBox>
                  <ServiceIcon>📱</ServiceIcon>
                </ServiceIconBox>
                <ServiceTitle>Mobile Apps</ServiceTitle>
                <ServiceDescription>
                  Native and cross-platform mobile applications that deliver exceptional user experiences.
                </ServiceDescription>
              </ServiceCard>
              <ServiceCard>
                <ServiceIconBox>
                  <ServiceIcon>☁️</ServiceIcon>
                </ServiceIconBox>
                <ServiceTitle>Cloud Solutions</ServiceTitle>
                <ServiceDescription>
                  Scalable cloud infrastructure and services to power your business growth.
                </ServiceDescription>
              </ServiceCard>
            </ServicesGrid>
          </ServicesContainer>
        </ServicesSection>

        {/* CTA Section */}
        <CTASection>
          <CTAContainer>
            <CTAContent>
              <CTATitle>Ready to Transform Your Business?</CTATitle>
              <CTADescription>
                Let's discuss how we can help you achieve your digital goals. 
                Book a free consultation today.
              </CTADescription>
              <CTAButtons>
                <PrimaryButton onClick={openModal}>
                  Book a Call
                  <ButtonArrow>→</ButtonArrow>
                </PrimaryButton>
                <ContactInfo>
                  <PhoneIcon>📞</PhoneIcon>
                  <PhoneNumber href="tel:313-509-8414">313-509-8414</PhoneNumber>
                </ContactInfo>
              </CTAButtons>
            </CTAContent>
          </CTAContainer>
        </CTASection>

        {/* Footer */}
        <Footer>
          <FooterContainer>
            <FooterBrand>
              <FooterLogo>
                <img src={logoImg} alt="B&B Global Solutions" />
              </FooterLogo>
              <FooterTagline>
                "B&B Global Solutions sells time." Time you save improving your business, 
                time ahead of market trends and competitors.
              </FooterTagline>
            </FooterBrand>
            <FooterLinks>
              <FooterColumn>
                <FooterColumnTitle>Company</FooterColumnTitle>
                <FooterLink href="/classic">About Us</FooterLink>
                <FooterLink href="/software">Services</FooterLink>
                <FooterLink href="/direct">Portfolio</FooterLink>
              </FooterColumn>
              <FooterColumn>
                <FooterColumnTitle>Resources</FooterColumnTitle>
                <FooterLink href="/demo-video">Demo</FooterLink>
                <FooterLink href="/ai-webinar">AI Webinar</FooterLink>
                <FooterLink href="/calendly">Book a Call</FooterLink>
              </FooterColumn>
              <FooterColumn>
                <FooterColumnTitle>Contact</FooterColumnTitle>
                <FooterLink href="tel:313-509-8414">📞 313-509-8414</FooterLink>
                <FooterLink href="mailto:contact@bb-global-solutions.com">✉️ Email Us</FooterLink>
              </FooterColumn>
            </FooterLinks>
            <FooterBottom>
              <Copyright>© 2025 B&B Global Solutions. All rights reserved.</Copyright>
            </FooterBottom>
          </FooterContainer>
        </Footer>

        {/* Modal */}
        {isModalOpen && (
          <ModalOverlay onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Book a Discovery Call</ModalTitle>
                <CloseButton onClick={closeModal}>&times;</CloseButton>
              </ModalHeader>
              <CalendlyContainer>
                {!calendlyLoaded && (
                  <CalendlyLoading>
                    <LoadingSpinner />
                    <LoadingText>Loading Calendar...</LoadingText>
                  </CalendlyLoading>
                )}
                <div 
                  className="calendly-inline-widget" 
                  data-url="https://calendly.com/bb-global-solutions/discovery"
                  style={{ 
                    width: '100%', 
                    height: '700px',
                    opacity: calendlyLoaded ? 1 : 0,
                    transition: 'opacity 0.3s ease'
                  }}
                />
              </CalendlyContainer>
            </ModalContent>
          </ModalOverlay>
        )}
      </PageWrapper>
    </>
  );
};

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideText = keyframes`
  0% { opacity: 0; transform: translateX(-20px); }
  10% { opacity: 1; transform: translateX(0); }
  90% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; transform: translateX(20px); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
`;

const scrollLogos = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(200, 30, 30, 0.4); }
  50% { transform: scale(1.02); box-shadow: 0 0 20px 5px rgba(200, 30, 30, 0.2); }
`;

// Global Styles
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Source+Sans+Pro:wght@300;400;600;700&display=swap');
  
  body {
    margin: 0;
    padding: 0;
    font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, sans-serif;
    background: #0a0a0a;
    color: #ffffff;
    overflow-x: hidden;
  }
  
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-family: 'Playfair Display', Georgia, serif;
  }
  
  ::-webkit-scrollbar {
    width: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: #1a1a1a;
  }
  
  ::-webkit-scrollbar-thumb {
    background: #c81e1e;
    border-radius: 4px;
  }
`;

// Page Wrapper
const PageWrapper = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
`;

// Navigation
const NavWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(10, 10, 10, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(200, 30, 30, 0.3);
`;

// Hero Section
const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 120px 5% 60px;
  position: relative;
  overflow: hidden;
`;

const GeometricBackground = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 55%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
  
  @media (max-width: 991px) {
    width: 100%;
    opacity: 0.3;
  }
`;

const GeometricPattern = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background: 
    linear-gradient(135deg, transparent 25%, rgba(200, 30, 30, 0.1) 25%, rgba(200, 30, 30, 0.1) 50%, transparent 50%),
    linear-gradient(225deg, transparent 25%, rgba(200, 30, 30, 0.15) 25%, rgba(200, 30, 30, 0.15) 50%, transparent 50%),
    linear-gradient(315deg, transparent 25%, rgba(200, 30, 30, 0.08) 25%, rgba(200, 30, 30, 0.08) 50%, transparent 50%),
    linear-gradient(45deg, transparent 25%, rgba(200, 30, 30, 0.12) 25%, rgba(200, 30, 30, 0.12) 50%, transparent 50%);
  background-size: 120px 120px, 100px 100px, 80px 80px, 140px 140px;
  background-position: 0 0, 20px 20px, 40px 40px, 60px 60px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 30px,
        rgba(200, 30, 30, 0.05) 30px,
        rgba(200, 30, 30, 0.05) 60px
      );
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to left, transparent, rgba(10, 10, 10, 1) 80%);
  }
`;

const HeroContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 1;
  
  @media (max-width: 991px) {
    flex-direction: column;
    text-align: center;
  }
`;

const HeroTextContent = styled.div`
  flex: 1;
  max-width: 600px;
  padding-right: 60px;
  
  @media (max-width: 991px) {
    padding-right: 0;
    margin-bottom: 40px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.8rem;
  font-weight: 700;
  line-height: 1.15;
  margin-bottom: 28px;
  color: #ffffff;
  animation: ${slideText} 6s ease-in-out infinite;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.15rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 35px;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 20px;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #c81e1e, #8b1515);
  color: white;
  padding: 16px 32px;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${pulse} 3s ease-in-out infinite;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 35px rgba(200, 30, 30, 0.4);
    animation: none;
  }
`;

const ButtonArrow = styled.span`
  transition: transform 0.3s ease;
  
  ${PrimaryButton}:hover & {
    transform: translateX(5px);
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: transparent;
  color: white;
  padding: 16px 32px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-3px);
  }
`;

const HeroImageContainer = styled.div`
  flex: 1;
  max-width: 500px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  
  @media (max-width: 991px) {
    max-width: 350px;
  }
`;

const HeroPersonImage = styled.div`
  width: 280px;
  height: 280px;
  background: radial-gradient(circle, rgba(200, 30, 30, 0.2), transparent);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  animation: ${float} 4s ease-in-out infinite;
  
  img {
    width: 180px;
    height: 180px;
    object-fit: contain;
    filter: drop-shadow(0 10px 30px rgba(200, 30, 30, 0.3));
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border: 2px dashed rgba(200, 30, 30, 0.3);
    border-radius: 50%;
    animation: spin 20s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const FloatingCard = styled.div`
  position: absolute;
  background: rgba(255, 255, 255, 0.95);
  padding: 12px 18px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  animation: ${float} 3s ease-in-out infinite;
  
  &:nth-child(2) { animation-delay: 0s; }
  &:nth-child(3) { animation-delay: 1s; }
  &:nth-child(4) { animation-delay: 2s; }
`;

const FloatingIcon = styled.span`
  font-size: 1.3rem;
`;

const FloatingText = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1a1a;
`;

const SliderDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 50px;
  position: relative;
  z-index: 1;
`;

const Dot = styled.button`
  width: ${props => props.$active ? '30px' : '12px'};
  height: 12px;
  border-radius: 6px;
  border: none;
  background: ${props => props.$active ? '#c81e1e' : 'rgba(255, 255, 255, 0.3)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.$active ? '#c81e1e' : 'rgba(255, 255, 255, 0.5)'};
  }
`;

// Logo Strip
const LogoStripSection = styled.section`
  background: linear-gradient(to right, #f5f5f5, #ffffff, #f5f5f5);
  padding: 30px 0;
  overflow: hidden;
`;

const LogoTrack = styled.div`
  display: flex;
  animation: ${scrollLogos} 30s linear infinite;
  width: fit-content;
`;

const LogoItem = styled.div`
  flex-shrink: 0;
  padding: 0 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoImage = styled.img`
  height: 40px;
  filter: grayscale(100%);
  opacity: 0.6;
  transition: all 0.3s ease;
  
  &:hover {
    filter: grayscale(0%);
    opacity: 1;
  }
`;

// Trusted Section
const TrustedSection = styled.section`
  padding: 100px 5%;
  background: #0f0f0f;
`;

const TrustedContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 80px;
  
  @media (max-width: 991px) {
    flex-direction: column;
  }
`;

const TrustedContent = styled.div`
  flex: 1;
`;

const SectionLabel = styled.span`
  display: inline-block;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 3px;
  color: #c81e1e;
  margin-bottom: 20px;
  text-transform: uppercase;
`;

const TrustedTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 25px;
  color: #ffffff;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const TrustedDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 30px;
`;

const TrustedFeatures = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 35px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FeatureIcon = styled.span`
  width: 24px;
  height: 24px;
  background: rgba(200, 30, 30, 0.2);
  color: #c81e1e;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
`;

const FeatureText = styled.span`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #c81e1e, #8b1515);
  color: white;
  padding: 16px 36px;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 35px rgba(200, 30, 30, 0.4);
  }
`;

const TrustedVisual = styled.div`
  flex: 1;
  position: relative;
  
  @media (max-width: 991px) {
    width: 100%;
    max-width: 600px;
  }
`;

const DashboardMockup = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const DashboardHeader = styled.div`
  background: #252525;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const DashboardDots = styled.div`
  display: flex;
  gap: 8px;
`;

const DashboardContent = styled.div`
  display: flex;
  min-height: 300px;
`;

const DashboardSidebar = styled.div`
  width: 160px;
  background: #1f1f1f;
  padding: 20px 0;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 600px) {
    display: none;
  }
`;

const SidebarItem = styled.div`
  padding: 12px 20px;
  font-size: 0.85rem;
  color: ${props => props.$active ? '#c81e1e' : 'rgba(255, 255, 255, 0.6)'};
  background: ${props => props.$active ? 'rgba(200, 30, 30, 0.1)' : 'transparent'};
  border-left: ${props => props.$active ? '3px solid #c81e1e' : '3px solid transparent'};
  transition: all 0.3s ease;
`;

const DashboardMain = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`;

const StatCard = styled.div`
  background: #252525;
  padding: 20px;
  border-radius: 8px;
  flex: 1;
  min-width: 120px;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
`;

const ChartPlaceholder = styled.div`
  width: 100%;
  height: 120px;
  background: #252525;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

const ChartBar = styled.div`
  flex: 1;
  background: linear-gradient(to top, #c81e1e, #ff4444);
  border-radius: 4px 4px 0 0;
  transition: height 0.3s ease;
`;

const FloatingElement = styled.div`
  position: absolute;
  animation: ${float} 3s ease-in-out infinite;
`;

const PlantIcon = styled.span`
  font-size: 2.5rem;
`;

// Services Section
const ServicesSection = styled.section`
  padding: 100px 5%;
  background: #0a0a0a;
`;

const ServicesContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  text-align: center;
`;

const ServicesTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 700;
  margin-bottom: 60px;
  color: #ffffff;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    margin-bottom: 40px;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: #151515;
  padding: 40px 30px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    border-color: rgba(200, 30, 30, 0.3);
    box-shadow: 0 20px 40px rgba(200, 30, 30, 0.15);
  }
`;

const ServiceIconBox = styled.div`
  width: 70px;
  height: 70px;
  background: rgba(200, 30, 30, 0.1);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
`;

const ServiceIcon = styled.span`
  font-size: 2rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: #ffffff;
  font-family: 'Source Sans Pro', sans-serif;
`;

const ServiceDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.6);
`;

// CTA Section
const CTASection = styled.section`
  padding: 100px 5%;
  background: linear-gradient(135deg, #1a0505, #2a0a0a);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 50%, rgba(200, 30, 30, 0.15), transparent 50%);
  }
`;

const CTAContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const CTAContent = styled.div``;

const CTATitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: #ffffff;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 40px;
`;

const CTAButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 30px;
  
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PhoneIcon = styled.span`
  font-size: 1.3rem;
`;

const PhoneNumber = styled.a`
  font-size: 1.3rem;
  font-weight: 600;
  color: #ffffff;
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: #c81e1e;
  }
`;

// Footer
const Footer = styled.footer`
  background: #050505;
  padding: 80px 5% 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const FooterContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
`;

const FooterBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
  margin-bottom: 50px;
  padding-bottom: 50px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const FooterLogo = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  padding: 15px;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const FooterTagline = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.7);
  max-width: 600px;
`;

const FooterLinks = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-bottom: 50px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const FooterColumn = styled.div``;

const FooterColumnTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 20px;
  font-family: 'Source Sans Pro', sans-serif;
`;

const FooterLink = styled.a`
  display: block;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  margin-bottom: 12px;
  transition: color 0.3s ease;
  
  &:hover {
    color: #c81e1e;
  }
`;

const FooterBottom = styled.div`
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
`;

const Copyright = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.5);
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  border: 1px solid rgba(200, 30, 30, 0.3);
  max-width: 95vw;
  max-height: 95vh;
  width: 100%;
  max-width: 950px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  background: rgba(200, 30, 30, 0.1);
  border-bottom: 1px solid rgba(200, 30, 30, 0.2);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: scale(1.1);
  }
`;

const CalendlyContainer = styled.div`
  width: 100%;
  height: 700px;
  position: relative;
  
  .calendly-inline-widget {
    width: 100% !important;
    height: 700px !important;
  }
  
  @media (max-width: 768px) {
    height: 600px;
    
    .calendly-inline-widget {
      height: 600px !important;
    }
  }
`;

const CalendlyLoading = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #1a1a1a;
  z-index: 10;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(200, 30, 30, 0.3);
  border-top: 4px solid #c81e1e;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 16px;
`;

const LoadingText = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
`;

export default MashkraftHomePage;

